import { RequirementWorkflowType } from "@clipboard-health/contract-worker-app-bff";
import { formatDate } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import { ACCEPTABLE_UPLOAD_IMAGE_CONTENT_TYPES } from "@src/appV2/lib/files";
import { type WorkerRequirement } from "@src/appV2/redesign/Documents/api/types";
import { DOCUMENT_SCHEDULE_TB_TEST_FULL_PATH } from "@src/appV2/redesign/Documents/paths";
import { subMonths } from "date-fns";
import pluralize from "pluralize";
import { generatePath } from "react-router-dom";

import { TB_TEST_TYPE, type TbTestGuidedUploadStep, type TbTestType } from "./types";

export const TB_TEST_DATE_VALIDITY_MONTHS: Record<TbTestType, number> = {
  [TB_TEST_TYPE.TUBERCULIN_SKIN_TEST]: 12,
  [TB_TEST_TYPE.TB_BLOOD_TEST]: 12,
  [TB_TEST_TYPE.CHEST_X_RAY]: 60,
};

export const FOOTER_BUTTON_MIN_WIDTH = 100;

export const CONTENT_TYPES = ["application/pdf", ...ACCEPTABLE_UPLOAD_IMAGE_CONTENT_TYPES];

/**
 * Upload flow identifier used in standard document upload analytics events
 * (UPLOAD_DOCUMENT_SUCCESS / UPLOAD_DOCUMENT_FAILURE) to distinguish guided
 * uploads from the normal document upload flow.
 */
export const TB_TEST_GUIDED_UPLOAD_ANALYTICS_FLOW = "guided";

/** Steps where closing the dialog is not considered abandonment. */
export const TB_TEST_GUIDED_UPLOAD_TERMINAL_STEPS: TbTestGuidedUploadStep[] = ["success"];

export const TB_TEST_TYPE_LABELS: Record<TbTestType, string> = {
  [TB_TEST_TYPE.TUBERCULIN_SKIN_TEST]: "Tuberculin Skin Test",
  [TB_TEST_TYPE.TB_BLOOD_TEST]: "TB Blood Test",
  [TB_TEST_TYPE.CHEST_X_RAY]: "Chest X-ray",
};

/** Short descriptions shown in the test type selection step. */
export const TB_TEST_TYPE_DESCRIPTIONS: Record<TbTestType, string | undefined> = {
  [TB_TEST_TYPE.TUBERCULIN_SKIN_TEST]: undefined,
  [TB_TEST_TYPE.TB_BLOOD_TEST]: undefined,
  [TB_TEST_TYPE.CHEST_X_RAY]: "Required if you've ever tested positive for TB",
};

/** Detailed descriptions shown on the details page. */
export const TB_TEST_TYPE_DETAIL_DESCRIPTIONS: Record<TbTestType, string> = {
  [TB_TEST_TYPE.TUBERCULIN_SKIN_TEST]:
    "A small injection under the forearm skin, read by a provider 48\u201372 hours later. Results valid for 12 months.",
  [TB_TEST_TYPE.TB_BLOOD_TEST]:
    "Completed in a single blood draw, such as the Quantiferon\u00AE Gold Test (IGRA). Results valid for 12 months.",
  [TB_TEST_TYPE.CHEST_X_RAY]:
    "Required if you\u2019ve previously tested positive for TB. Results valid for 5 years.",
};

export const TB_TEST_UPLOAD_CALLOUT =
  "Downloading from a portal or health app? Get the full report instead of a screenshot so all the required information is included.";

export const TB_TEST_TYPES: TbTestType[] = [
  TB_TEST_TYPE.TUBERCULIN_SKIN_TEST,
  TB_TEST_TYPE.TB_BLOOD_TEST,
  TB_TEST_TYPE.CHEST_X_RAY,
];

export const TB_TEST_NOT_COMPLETED_RECENTLY_OPTION = "NOT_COMPLETED_RECENTLY" as const;

export const TB_TEST_TYPE_SELECTION_OPTIONS = [
  ...TB_TEST_TYPES,
  TB_TEST_NOT_COMPLETED_RECENTLY_OPTION,
] as const;

export type TestTypeSelectionOption = (typeof TB_TEST_TYPE_SELECTION_OPTIONS)[number];

export const TB_TEST_TYPE_SELECTION_LABELS: Record<TestTypeSelectionOption, string> = {
  ...TB_TEST_TYPE_LABELS,
  [TB_TEST_NOT_COMPLETED_RECENTLY_OPTION]: "I haven't completed one recently",
};

export const TB_TEST_TYPE_SELECTION_DESCRIPTIONS: Record<
  TestTypeSelectionOption,
  string | undefined
> = {
  ...TB_TEST_TYPE_DESCRIPTIONS,
  [TB_TEST_NOT_COMPLETED_RECENTLY_OPTION]: undefined,
};

/** Entry points that navigate to the Schedule TB Test page. Passed as a URL search param for analytics. */
export const SCHEDULE_TB_TEST_SOURCE = {
  /** "Schedule a TB Test" link on the document details page. */
  DETAILS_PAGE: "details_page",
  /** "I haven't completed one recently" option in the guided upload dialog. */
  NOT_COMPLETED_RECENTLY: "not_completed_recently",
  /** "Schedule a TB Test" link on the invalid test error step. */
  INVALID_TEST_ERROR: "invalid_test_error",
} as const;

export type ScheduleTbTestSource =
  (typeof SCHEDULE_TB_TEST_SOURCE)[keyof typeof SCHEDULE_TB_TEST_SOURCE];

const SCHEDULE_TB_TEST_SOURCE_VALUES = new Set<string>(Object.values(SCHEDULE_TB_TEST_SOURCE));

export function isScheduleTbTestSource(
  value: string | undefined | null
): value is ScheduleTbTestSource {
  return isDefined(value) && SCHEDULE_TB_TEST_SOURCE_VALUES.has(value);
}

export function generateScheduleTbTestPath(
  requirementId: string,
  source: ScheduleTbTestSource
): string {
  const path = generatePath(DOCUMENT_SCHEDULE_TB_TEST_FULL_PATH, { requirementId });
  return `${path}?source=${source}`;
}

export const SCHEDULE_TB_TEST_DISCLAIMER =
  "Clipboard does not cover the cost of TB testing, so any associated fees are your responsibility.";

export const TB_TEST_SCHEDULING_PROVIDERS = [
  {
    id: "cvs_pharmacy",
    name: "CVS Pharmacy",
    description:
      "Available nationwide. Select \u2018Tuberculosis (TB) Test\u2019 and enter your zip code to find a location near you.",
    buttonLabel: "Schedule with Minute Clinic",
    url: "https://www.cvs.com/minuteclinic/services/tb-testing",
  },
  {
    id: "little_clinic",
    name: "Little Clinic",
    description:
      "Available in specific states. Select \u2018Tuberculosis (TB) / PPD Test\u2019 to find a location near you.",
    buttonLabel: "Schedule with Little Clinic",
    url: "https://www.thelittleclinic.com/booking",
  },
  {
    id: "walgreens",
    name: "Walgreens",
    description:
      "Available in specific states. Use the \u2018Find Care\u2019 option to search for TB testing at a Walgreens near you.",
    buttonLabel: "Schedule with Walgreens Clinic",
    url: "https://www.walgreens.com/findcare/services",
  },
] as const;

export const TB_TEST_UPLOAD_OPTIONS = [
  {
    title: "\u201CTake a photo\u201D",
    description: "Take a photo of the result, making sure to capture all 4 corners of the paper.",
  },
  {
    title: "\u201CUpload from camera roll\u201D",
    description: "Upload an existing photo of the result from your camera roll.",
  },
  {
    title: "\u201CUpload from file manager\u201D",
    description:
      "Upload an existing test results file that was downloaded from an electronic records system (such as MyChart).",
    notes: [
      "Use official reports from a health portal rather than a screenshot.",
      "If you must use a screenshot, make sure to capture all relevant information.",
    ],
  },
] as const;

/** Returns the earliest acceptable test date based on the test type's validity period. */
export function getMinimumTestDate(testType: TbTestType): Date {
  return subMonths(new Date(), TB_TEST_DATE_VALIDITY_MONTHS[testType]);
}

/** Formats the validity period as a human-readable string, e.g. "12 months" or "5 years". */
export function formatValidityPeriod(testType: TbTestType): string {
  const months = TB_TEST_DATE_VALIDITY_MONTHS[testType];
  if (months > 12 && months % 12 === 0) {
    const years = months / 12;
    return `${years} ${pluralize("year", years)}`;
  }
  return `${months} ${pluralize("month", months)}`;
}

/**
 * Returns the checklist items for TB test instructions.
 * When a test type is provided, includes type-specific details (minimum date, read window).
 * When omitted, returns generic items suitable for the details page before test type selection.
 */
export function getTbTestChecklist(testType?: TbTestType): string[] {
  const items = ["First and last name (as it appears on Clipboard)"];

  if (isDefined(testType)) {
    switch (testType) {
      case TB_TEST_TYPE.TUBERCULIN_SKIN_TEST: {
        items.push(
          'Test result must explicitly indicate "Negative" or less than 14.9 mm induration'
        );
        break;
      }
      case TB_TEST_TYPE.TB_BLOOD_TEST: {
        items.push('Test result must explicitly indicate "Negative"');
        break;
      }
      case TB_TEST_TYPE.CHEST_X_RAY: {
        items.push('Test result must explicitly indicate "Negative for Tuberculosis"');
        break;
      }
      default: {
        return testType satisfies never;
      }
    }

    const formattedMinimumDate = formatDate(getMinimumTestDate(testType));
    items.push(`Test date after ${formattedMinimumDate}`);
  } else {
    items.push("Negative test result", "Test date");
  }

  if (testType === TB_TEST_TYPE.TUBERCULIN_SKIN_TEST) {
    items.push("Result read 48–72 hours after administration");
  }

  items.push("Provider's signature or clinic information (name, phone, and address)");

  return items;
}

export function isTbTestGuidedUploadRequirement(
  requirement: Pick<WorkerRequirement, "requirement">
): boolean {
  return (
    requirement.requirement?.attributes.requirementWorkflowType ===
    RequirementWorkflowType.TB_TEST_GUIDED_UPLOAD
  );
}
